import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../state/AuthContext";
import AddFollowupModal from "../components/AddFollowupModal";
import EditLeadModal from "../components/EditLeadModal";
import PaymentReceiptModal from "../components/PaymentReceiptModal";

export default function AdminDashboardPage() {
  const { http } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);

  // Today's Followups state
  const [todaysFollowups, setTodaysFollowups] = useState([]);
  const [followupSummary, setFollowupSummary] = useState({
    totalDue: 0,
    dueToday: 0,
    overdue: 0,
  });

  // Selected item for modals
  const [selectedForFollowup, setSelectedForFollowup] = useState(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [selectedForPayment, setSelectedForPayment] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardRes, listRes, todaysRes] = await Promise.all([
        http.get("/api/admin/dashboard"),
        http.get("/api/enquiries", { params: { status: "NEW" } }),
        http.get("/api/dashboard/todays-followups"),
      ]);
      setStats(dashboardRes.data);
      setRows(listRes.data.items || []);
      setTodaysFollowups(todaysRes.data.items || []);
      if (todaysRes.data.summary) {
        setFollowupSummary(todaysRes.data.summary);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  }, [http]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openWhatsApp = (item) => {
    const mobileClean = String(item.customerMobile || "").replace(/\D/g, "");
    const formattedPhone = mobileClean.length === 10 ? `91${mobileClean}` : mobileClean;
    const msg = `Hello ${item.customerName}, regarding your RTO Application ${item.applicationNo} (${item.latestWorkTypeName || "RTO Work"}). Please let us know if you have any questions or document updates. Thank you!`;
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(msg)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="d-grid gap-3">
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <h4 className="mb-0">Admin Dashboard 🛡️</h4>
          <div className="text-muted small">Manage masters, lead entries, follow-ups, reports, and system settings.</div>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={loadData}>
          Refresh
        </button>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* TODAY'S FOLLOW-UPS HIGHLIGHT */}
      <div className="card shadow-sm border-start border-warning border-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="card-title mb-0 fw-bold text-dark">Today's Scheduled Follow-ups 📅</h5>
              <div className="text-muted small">
                Active leads requiring follow-up action or scheduled appointments today.
              </div>
            </div>
            <div className="d-flex gap-2">
              <span className="badge text-bg-warning fs-6">
                Today: {followupSummary.dueToday}
              </span>
              <span className="badge text-bg-danger fs-6">
                Overdue: {followupSummary.overdue}
              </span>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Application No</th>
                  <th>Customer Name</th>
                  <th>Mobile</th>
                  <th>Work Details</th>
                  <th>Followup Due Date</th>
                  <th>Assigned To</th>
                  <th>Balance (₹)</th>
                  <th>Schedule</th>
                  <th className="text-end" style={{ minWidth: "280px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-3 text-muted">
                      Checking today's followups...
                    </td>
                  </tr>
                ) : null}

                {!loading && todaysFollowups.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-3 text-muted">
                      🎉 No follow-ups scheduled or due for today!
                    </td>
                  </tr>
                ) : null}

                {!loading &&
                  todaysFollowups.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-bold">{item.applicationNo}</td>
                      <td className="fw-bold text-uppercase">{item.customerName}</td>
                      <td>
                        <a href={`tel:${item.customerMobile}`} className="text-decoration-none">
                          📱 {item.customerMobile}
                        </a>
                      </td>
                      <td>
                        <div className="fw-semibold small">{item.latestWorkTypeName || "-"}</div>
                        <div className="text-muted small">
                          {item.latestWorkDescription || item.latestWorkName || "-"}
                        </div>
                        {item.vehicleNo ? (
                          <div className="badge text-bg-light border text-dark mt-1">
                            🚗 {item.vehicleNo}
                          </div>
                        ) : null}
                      </td>
                      <td className="small">
                        {item.nextFollowupAt || item.nextAppointmentAt ? (
                          <span className="fw-semibold">
                            {new Date(item.nextFollowupAt || item.nextAppointmentAt).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{item.assignedToName || "Unassigned"}</td>
                      <td className="fw-bold text-danger">
                        ₹{Number(item.pendingBalance || 0).toFixed(2)}
                      </td>
                      <td>
                        {item.scheduleStatus === "TODAY" ? (
                          <span className="badge text-bg-warning">DUE TODAY</span>
                        ) : item.scheduleStatus === "OVERDUE" ? (
                          <span className="badge text-bg-danger">OVERDUE</span>
                        ) : (
                          <span className="badge text-bg-secondary">{item.scheduleStatus}</span>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-primary"
                            title="Add Progress Followup"
                            onClick={() => setSelectedForFollowup(item)}
                          >
                            📝 Followup
                          </button>
                          <button
                            type="button"
                            className="btn btn-success"
                            title="WhatsApp Chat"
                            onClick={() => openWhatsApp(item)}
                          >
                            💬 WhatsApp
                          </button>
                          <button
                            type="button"
                            className="btn btn-warning text-dark"
                            title="Edit Lead"
                            onClick={() => setSelectedForEdit(item)}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-success"
                            title="Payments & Receipts"
                            onClick={() => setSelectedForPayment(item)}
                          >
                            💳 Payment
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {stats ? (
        <div className="row g-3">
          <div className="col-12 col-md-4">
            <div className="card">
              <div className="card-body py-2">
                <div className="text-muted small">New Enquiries</div>
                <div className="fs-4 fw-semibold text-primary">{stats.newEnquiries}</div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="card">
              <div className="card-body py-2">
                <div className="text-muted small">In Progress</div>
                <div className="fs-4 fw-semibold text-warning">{stats.inProgress}</div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="card">
              <div className="card-body py-2">
                <div className="text-muted small">Completed</div>
                <div className="fs-4 fw-semibold text-success">{stats.completed}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 fw-bold">New Unassigned Enquiries</h6>
            <span className="text-muted small">
              {loading ? "Loading..." : `${rows.length} items`}
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Application No</th>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Created Date</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted py-3">
                      No new unassigned enquiries.
                    </td>
                  </tr>
                ) : null}
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="fw-semibold">{r.applicationNo}</td>
                    <td className="text-uppercase">{r.customerName}</td>
                    <td>{r.customerMobile}</td>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td className="text-end">
                      <Link
                        className="btn btn-outline-primary btn-sm"
                        to={`/admin/enquiries/${r.id}`}
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODALS */}
      {selectedForFollowup ? (
        <AddFollowupModal
          enquiry={selectedForFollowup}
          onClose={() => setSelectedForFollowup(null)}
          onSaved={loadData}
        />
      ) : null}

      {selectedForEdit ? (
        <EditLeadModal
          enquiry={selectedForEdit}
          onClose={() => setSelectedForEdit(null)}
          onSaved={loadData}
        />
      ) : null}

      {selectedForPayment ? (
        <PaymentReceiptModal
          enquiry={selectedForPayment}
          onClose={() => setSelectedForPayment(null)}
          onPaymentRecorded={loadData}
        />
      ) : null}
    </div>
  );
}
